import { Modal, Form, Input, Button } from "antd";
import { useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";

import { formItemLayout } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import Loading from "../../../common/Loading";
import { General } from "../../../../util/General";
import FormInputRules from "../../../../util/FormInputRules";
import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";
import { ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";
import { useSelector } from "react-redux";
import { RootState } from "../../../../appRedux/store";
import { CustomNotification } from "../../../../util/CustomNotification";

type AdvanceBookingQuotaMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const AdvanceBookingQuotaMaster = (props: AdvanceBookingQuotaMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);

  /* CUSTOM HOOKS */
  const { bounds, onStart } = useReactDraggable();
  const { loading: vehicleTypeListLoading, masterList: vehicleTypeMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.VEHICLE_TYPE_MASTER]);
  const { loading: advanceBookingQuotaListLoading, masterList: advanceBookingQuotaMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.ADVANCE_BOOKING_QUOTA_MASTER]);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* HANDLERS */

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !(props.addRecord && !vehicleTypeListLoading && !advanceBookingQuotaListLoading);
  const isOnEditLoading = !(!props.addRecord && !vehicleTypeListLoading && !advanceBookingQuotaListLoading);

  const handleFinish = (values: any) => {
    if (props.addRecord) {
      const vehicleTypeQuota = vehicleTypeMasterList
        ?.filter((item: any) => {
          const quota = values[`advance_booking_quota_${item.id}`];
          return quota !== undefined && quota !== null && quota !== "";
        })
        .map((item: any) => ({
          vehicle_type_id: item.id,
          advance_booking_quota: Number(values[`advance_booking_quota_${item.id}`])
        }));

      if (!vehicleTypeQuota || vehicleTypeQuota.length === 0) {
        CustomNotification.showErrorMessage("Please enter at least one advance booking quota.");
        return;
      }

      props.handleSubmit({
        customer_id: masterFilters?.customerId,
        customer_location_id: masterFilters?.locationId,
        vehicleTypeQuota
      });
    } else {
      if (values?.advance_booking_quota === undefined || values?.advance_booking_quota === "") {
        CustomNotification.showErrorMessage("Advance booking quota is required.");
        return;
      }

      props.handleSubmit({
        customer_id: masterFilters?.customerId,
        customer_location_id: masterFilters?.locationId,
        vehicle_type_id: props.rowData?.vehicle_type_id,
        advance_booking_quota: values?.advance_booking_quota
      });
    }
  };

  const availableVehicleTypes = vehicleTypeMasterList?.filter((item: any) => {
    return !advanceBookingQuotaMasterList?.some((exist: any) => exist.vehicle_type_id === item.id && exist.customer_location_id === masterFilters?.locationId);
  });

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue({
        ...props.rowData,
        vehicle_type_name: props.rowData?.vehicleType?.vehicle_type_name
      });
      setFormFieldsLoadedToForm(true);
    }
    // eslint-disable-next-line
  }, [isOnEditLoading]);

  return (
    <Modal
      afterClose={() => form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} Advance Booking Quota Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"50%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && isOnAddLoading) || (!props.addRecord && !formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !isOnAddLoading) || (!props.addRecord && formFieldsLoadedToForm)) && (
        <Form
          ref={formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]}`}
          {...formItemLayout}
          form={form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={handleFinish}
        >
          {props.addRecord ? (
            <div className={styles.scrollContainer}>
              <div className={styles.vehicleQuotaTable}>
                <div className={styles.tableHeader}>
                  <div>Vehicle Type</div>
                  <div>Advance Booking Quota</div>
                </div>
                {availableVehicleTypes?.length > 0 ? (
                  availableVehicleTypes.map((item: any) => (
                    <div key={item?.id} className={styles.tableRow}>
                      <div>
                        <Form.Item noStyle>
                          <Input value={item?.vehicle_type_name} disabled />
                        </Form.Item>
                      </div>
                      <div>
                        <Form.Item name={`advance_booking_quota_${item?.id}`} normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.allowOnlyDigitsRule("advance booking quota")]}>
                          <Input type="number" placeholder="Enter quota" />
                        </Form.Item>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: "16px", fontStyle: "italic", color: "#999" }}>All vehicle types already have an advance booking quota assigned.</div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className={styles.scrollContainer}>
                <div className={styles.vehicleQuotaTable}>
                  <div className={styles.tableHeader}>
                    <div>Vehicle Type</div>
                    <div>Advance Booking Quota</div>
                  </div>
                  <div className={styles.tableRow}>
                    <div>
                      <Form.Item noStyle>
                        <Input value={props.rowData?.vehicleType?.vehicle_type_name} disabled />
                      </Form.Item>
                    </div>
                    <div>
                      <Form.Item name={`advance_booking_quota`} normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.allowOnlyDigitsRule("advance booking quota")]}>
                        <Input type="number" placeholder="Enter quota" />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {((props?.addRecord && availableVehicleTypes?.length > 0) || !props?.addRecord) && (
            <>
              <Form.Item className={`${styles["form-itemFloatR20"]}`}>
                <Button htmlType="submit" className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
                  {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
                </Button>
              </Form.Item>

              <Form.Item className={`${styles["form-itemFloatR20"]}`}>
                <Button disabled={props.saving} className={`gx-mb-0  ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
                  Close
                </Button>
              </Form.Item>
            </>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default AdvanceBookingQuotaMaster;
